import { IssueStatus } from '@/entities/todo';
import { IssuePriority, IssueType } from '@/entities/issue';
import * as q from '@/services/api/request/query';
import { SortableQuery } from '@/services/api/request/query';
import { encodePaginatableQuery, PaginatableQuery, Query } from '../../request';

export const unsetSort = (query: AnalyticsQuery) => {
  const queryNoSort = { ...query };
  queryNoSort.sort = '';
  queryNoSort.dir = '';
  return queryNoSort;
};

export interface AnalyticsQuery extends PaginatableQuery, SortableQuery {
  timezone: string;
  start: Date;
  end: Date;
}

export interface InspectionQuery extends AnalyticsQuery {
  entityItem: number[];
  group: number[];
  assignee: number[];
  category: number[];
  checklist: number[];
  overdue: boolean;
  excludeZeroOverdueInspectionCount?: boolean;
  response: string[];
  section: string[];
  questionTitle: string[];
  status?: number[];
}

export interface GeneralInspectionQuery extends InspectionQuery {
  doneBy: number[];
}

export interface IssueQuery extends AnalyticsQuery {
  entityItem: number[];
  group: number[];
  creator: number[];
  assignee: number[];
  resolver: number[];
  category: number[];
  checklist: number[];
  question: string[];
  questionTitle: string[];
  response: string[];
  inspection: number[];
  section: string[];
  type: IssueType[];
  priority: IssuePriority[];
  status: IssueStatus[];
  sensor: number[];
  overdue?: boolean;
}

export interface QuestionQuery extends AnalyticsQuery {
  entityItem: number[];
  group: number[];
  inspector: number[];
  category: number[];
  checklist: number[];
  question: string[];
  questionTitle: string[];
  response: string[];
  inspection: number[];
  section: string[];
  hasScore: boolean;
  excludeNegativeResponseCount: boolean;
}

export interface ChecklistExplainQuery {
  uuid: string;
  response: string;
  type: string;
}

export interface TabQuery {
  tab: string;
}

const encodeAnalyticsQuery = (query: AnalyticsQuery): Query => ({
  ...encodePaginatableQuery(query),
  timezone: q.string(query.timezone),
  sort: query.sort ? q.sortEncoder(query.sort, query.dir) : '',
  start: q.date(query.start),
  end: q.date(query.end),
});

export const encodeInspectionQuery = (query: InspectionQuery): Query => ({
  ...encodeAnalyticsQuery(query),
  entityItem: q.numbers(query.entityItem),
  group: q.numbers(query.group),
  assignee: q.numbers(query.assignee),
  category: q.numbers(query.category),
  checklist: q.numbers(query.checklist),
  overdue: q.boolean(query.overdue),
  response: q.strings(query.response),
  section: q.strings(query.section),
  questionTitle: q.strings(query.questionTitle),
  excludeZeroOverdueInspectionCount: q.nullableBoolean(query.excludeZeroOverdueInspectionCount ?? null),
  status: q.numbers(query.status ?? []),
});

export const encodeGeneralInspectionQuery = (query: GeneralInspectionQuery): Query => ({
  ...encodeInspectionQuery(query),
  doneBy: q.numbers(query.doneBy),
});

export const encodeIssueQuery = (query: IssueQuery): Query => ({
  ...encodeAnalyticsQuery(query),
  entityItem: q.numbers(query.entityItem),
  group: q.numbers(query.group),
  creator: q.numbers(query.creator),
  assignee: q.numbers(query.assignee),
  resolver: q.numbers(query.resolver),
  category: q.numbers(query.category),
  checklist: q.numbers(query.checklist),
  question: q.strings(query.question),
  //title in the API
  title: q.strings(query.questionTitle),
  response: q.strings(query.response),
  inspection: q.numbers(query.inspection),
  section: q.strings(query.section),
  type: q.stringEnums(query.type),
  priority: q.numericEnums(query.priority),
  status: q.numericEnums(query.status),
  sensor: q.numbers(query.sensor),
  overdue: query.overdue !== undefined ? q.boolean(query.overdue) : '',
});

export const encodeQuestionQuery = (query: QuestionQuery): Query => ({
  ...encodeAnalyticsQuery(query),
  entityItem: q.numbers(query.entityItem),
  group: q.numbers(query.group),
  inspector: q.numbers(query.inspector),
  category: q.numbers(query.category),
  checklist: q.numbers(query.checklist),
  question: q.strings(query.question),
  questionTitle: q.strings(query.questionTitle),
  response: q.strings(query.response),
  inspection: q.numbers(query.inspection),
  section: q.strings(query.section),
  hasScore: q.boolean(query.hasScore),
  excludeNegativeResponseCount: q.boolean(query.excludeNegativeResponseCount),
});

export const encodeChecklistExplainQuery = (query: ChecklistExplainQuery): Query => ({
  uuid: q.string(query.uuid),
  //might be strings
  response: q.string(query.response),
  type: q.string(query.type),
});

export const encodeTabQuery = (query: TabQuery): Query => ({
  tab: q.string(query.tab),
});
